import { useEffect, useState } from 'react';

import useSymptomChecker from 'hooks/useSymptomChecker';
import { ENGLISH_YES, SPANISH_YES } from 'lib/constants';
import { checkSpanishLocale, cn } from 'lib/utils';
import { useSymptomStore } from 'store/symptom';
import SymptomContainer from './components/SymptomContainer';
import { motion } from 'framer-motion';
import { Input } from 'components/ui/input';
import Icon from 'components/icon/Icon';
import { useTranslation } from 'react-i18next';

const checkIsYes = (value: string) => {
  return [ENGLISH_YES.toLowerCase(), SPANISH_YES.toLowerCase(), 'sí'].includes(
    value.toLowerCase()
  );
};

export default function SymptomStartedMedication() {
  const { t } = useTranslation();
  const questionData = useSymptomStore((store) => store.questionData)?.data;

  const isSpanish = checkSpanishLocale();

  const { onSubmit, isLoading } = useSymptomChecker();

  const englishChoices = JSON.parse(questionData?.eng_choices || '[]');
  const spanishChoices = JSON.parse(questionData?.spanish_choices || '[]');

  const options = isSpanish ? spanishChoices : englishChoices;
  const formattedOptions = options.map((c: unknown) =>
    typeof c === 'string' ? c : Object.keys(c as any)[0]
  );

  const [selectedOption, setSelectedOption] = useState(formattedOptions[0]);
  const [medication, setMedication] = useState('');

  useEffect(() => {
    if (questionData?.user_eng_choices || questionData?.user_spanish_choices) {
      const selected =
        questionData?.[isSpanish ? 'user_spanish_choices' : 'user_eng_choices'];
      let parsedAnswer = '';
      try {
        parsedAnswer = JSON.parse(selected);
      } catch (error) {
        console.log(error);
      }

      if (typeof parsedAnswer === 'string') {
        setMedication(parsedAnswer);
      } else {
        setSelectedOption(Object.keys(parsedAnswer)[0]);
        setMedication(Object.values(parsedAnswer)[0] as string);
      }
    }
  }, [questionData, isSpanish]);

  const onSelectOption = (option: string) => {
    setSelectedOption(option);
    setMedication('');
  };

  const onContinue = () => {
    let englishChoice = '',
      spanishChoice = '';
    if (checkIsYes(selectedOption)) {
      englishChoice = JSON.stringify({ [ENGLISH_YES]: medication });
      spanishChoice = JSON.stringify({ [SPANISH_YES]: medication });
    } else {
      const index = isSpanish
        ? spanishChoices.indexOf(selectedOption)
        : englishChoices.indexOf(selectedOption);
      englishChoice = englishChoices[index];
      spanishChoice = spanishChoices[index];
    }

    onSubmit({
      eng_choices: englishChoice,
      spanish_choices: spanishChoice,
      questionData,
    });
  };

  return (
    <SymptomContainer
      questionData={questionData}
      isLoading={isLoading}
      onContinue={onContinue}
      questionNumber={6}
    >
      <div className='grid gap-4 md:gap-6 md:grid-cols-2'>
        {formattedOptions.map((option: string) => {
          const isSelected = selectedOption === option;
          return (
            <div key={option} className='space-y-2'>
              <motion.div
                onClick={() => onSelectOption(option)}
                className={cn(
                  'w-full border-[2px] rounded-2xl px-4 py-2 flex items-center justify-between cursor-pointer text-[#656D76] gap-4 transition-all duration-150',
                  isSelected && 'border-primary text-primary bg-primary/10'
                )}
                whileTap={{ scale: 0.95 }}
                transition={{ duration: 0.15 }}
              >
                <div className='w-full'>
                  <div className='flex justify-end pt-2 pb-3'>
                    <Icon
                      icon={
                        checkIsYes(option) ? 'drug-container' : 'shield-health'
                      }
                      size={24}
                      className={isSelected ? 'text-primary' : 'text-[#656D76]'}
                    />
                  </div>
                  <p className='text-sm font-medium leading-5'>{option}</p>
                </div>
              </motion.div>

              {checkIsYes(selectedOption) && isSelected && (
                <Input
                  placeholder={`${t('mention_medications')}...`}
                  value={medication}
                  onChange={(e) => setMedication(e.target.value)}
                  autoFocus
                  className='text-sm'
                />
              )}
            </div>
          );
        })}
      </div>
    </SymptomContainer>
  );
}
